import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button, Modal, Alert } from "react-bootstrap";
import Layout from "../../components/Layout";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ToastFile from "../../utils/ToastFile";
import {
  getEmployeeDetails,
  updateEmployeeProfile,
  changeEmployeePassword,
  uploadEmployeeImage,
} from "../../api/EmployeeApi";
import { MdEdit, MdSave, MdCancel, MdCameraAlt } from "react-icons/md";
import { FaEye, FaEyeSlash, FaUser } from "react-icons/fa";
import "./Profile.css";
import { changePassword } from "../../api/authApi";
import moment from "moment";

const EmployeeProfile = () => {
  const [employee, setEmployee] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const employeeId = user?.id;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    watch: watchPassword,
    formState: { errors: passwordErrors },
  } = useForm();

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeProfile();
    }
  }, [employeeId]);

  const fetchEmployeeProfile = async () => {
    try {
      const response = await getEmployeeDetails(employeeId);
      if (response.status === 200) {
        let employeeData = response.data.data;
      
        employeeData.license_exp_date = employeeData.license_exp_date
          ? moment(employeeData.license_exp_date).format("YYYY-MM-DD")
          : null;
          employeeData.createdAt = moment(employeeData.createdAt).format("YYYY-MM-DD");
        setEmployee(employeeData);
        reset(employeeData);
        setLoading(false);
      } else {
        ToastFile("Failed to fetch profile", "error");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      ToastFile("Error fetching profile", "error");
      setLoading(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        ToastFile("Image size should be less than 5MB", "error");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        ToastFile("Please select a valid image file (JPEG, PNG, GIF)", "error");
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) {
      ToastFile("Please select an image first", "error");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("image", selectedImage);

    try {
      const response = await uploadEmployeeImage(employeeId, formData);
      if (response.status === 200) {
        ToastFile("Profile image updated successfully", "success");
        setSelectedImage(null);
        setImagePreview(null);
        fetchEmployeeProfile(); // Refresh profile data
      } else {
        ToastFile("Failed to upload image", "error");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      ToastFile("Error uploading image", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const onProfileSubmit = async (data) => {
    try {
      const payload = {
        ...employee,
        name: data.name,
        email: data.email,
        contact_no: data.contact_no,
        address: data.address,
        abn: data.abn,
        // license_no: data.license_no,
        license_exp_date: data.license_exp_date,
        designation: data.designation,
        payrate: data.payrate,
        modifiedBy: employeeId,
        modifiedAt: new Date(),
      };

      const response = await updateEmployeeProfile(employeeId, payload);
      if (response.status === 200) {
        ToastFile("Profile updated successfully", "success");
        setIsEditing(false);
        fetchEmployeeProfile();
      } else {
        ToastFile("Failed to update profile", "error");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      ToastFile("Error updating profile", "error");
    }
  };

  const onPasswordSubmit = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      ToastFile("New passwords do not match", "error");
      return;
    }

    try {
      const payload = {
        email: employee.email,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        modifiedBy: employeeId,
        modifiedAt: new Date(),
      };

      const response = await changePassword(user.user_id, payload);
      if (response.status === 200) {
        ToastFile("Password changed successfully", "success");
        setIsChangingPassword(false);
        resetPassword();
      } else if (response.status === 401) {
        ToastFile("Current Password is invalid", "error");
      } else {
        ToastFile("Failed to change password", "error");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      ToastFile("Error changing password", "error");
    }
  };

  const togglePasswordVisibility = (field) => {
    setIsPasswordVisible((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(employee);
  };

  const handleCancelPassword = () => {
    setIsChangingPassword(false);
    resetPassword();
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3">
        <div className="profile-header d-flex justify-content-between align-items-center">
          <span
            style={{
              fontSize: "22px",
              fontWeight: 600,
              minWidth: "140px",
            }}
          >
          My Profile
          </span>
        </div>

        <Row>
          {/* Profile Image Section */}
          <Col lg={4} md={12} className="mb-4">
            <Card className="profile-card text-center">
              <Card.Body>
                <div className="profile-image-container mb-3">
                  {imagePreview || employee?.profile_image ? (
                    <img
                      src={imagePreview || employee?.profile_image}
                      alt="Profile"
                      className="rounded-circle profile-image"
                      style={{
                        width: "150px",
                        height: "150px",
                        objectFit: "cover",
                        border: "3px solid #dee2e6",
                      }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                      style={{
                        width: "150px",
                        height: "150px",
                        backgroundColor: "#f8f9fa",
                        border: "3px solid #dee2e6",
                      }}
                    >
                      <FaUser size={60} color="#6c757d" />
                    </div>
                  )}

                  <Button
                    variant="primary"
                    size="sm"
                    className="position-absolute bottom-0 end-0 rounded-circle camera-button"
                    style={{ width: "40px", height: "40px" }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <MdCameraAlt />
                  </Button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  style={{ display: "none" }}
                />

                {selectedImage && (
                  <div className="upload-buttons">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={uploadImage}
                      disabled={isUploading}
                      className="me-2"
                    >
                      {isUploading ? "Uploading..." : "Upload Image"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <h5 className="mt-3 mb-1">{employee?.name}</h5>
                <p className="text-muted mb-0">{employee?.email}</p>
                <p className="text-muted mb-1">{employee?.designation}</p>
                <span
                  className={`badge status-badge ${
                    employee?.status === "Active" ? "bg-success" : "bg-danger"
                  }`}
                >
                  {employee?.status}
                </span>
              </Card.Body>
            </Card>
          </Col>

          {/* Profile Details Section */}
          <Col lg={8} md={12}>
            <Card className="profile-info-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Profile Information</h5>
                {!isEditing ? (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="edit-button"
                  >
                    <MdEdit className="me-1" />
                    Edit Profile
                  </Button>
                ) : (
                  <div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSubmit(onProfileSubmit)}
                      className="me-2"
                    >
                      <MdSave className="me-1" />
                      Save
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      <MdCancel className="me-1" />
                      Cancel
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("name", {
                            required: "Name is required",
                          })}
                          disabled={!isEditing}
                          isInvalid={errors.name}
                        />
                        {errors.name && (
                          <Form.Control.Feedback type="invalid">
                            {errors.name.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                          disabled
                          isInvalid={errors.email}
                        />
                        {errors.email && (
                          <Form.Control.Feedback type="invalid">
                            {errors.email.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Number</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("contact_no", {
                            required: false,
                          })}
                          disabled={!isEditing}
                          isInvalid={errors.contact_no}
                        />
                        {errors.contact_no && (
                          <Form.Control.Feedback type="invalid">
                            {errors.contact_no.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Control
                          type="text"
                          value={employee?.status}
                          disabled
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ABN</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("abn", { required: false })}
                          disabled={!isEditing}
                          isInvalid={errors.abn}
                        />
                        {errors.abn && (
                          <Form.Control.Feedback type="invalid">
                            {errors.abn.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Driving License Exp Date</Form.Label>
                        <Form.Control
                          type="date"
                          {...register("license_exp_date", {
                            required: false,
                          })}
                          disabled={!isEditing}
                          isInvalid={errors.license_exp_date}
                        />
                        {errors.license_exp_date && (
                          <Form.Control.Feedback type="invalid">
                            {errors.license_exp_date.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    {/* <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>License Number</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("license_no", {
                            required: "License number is required",
                          })}
                          disabled={!isEditing}
                          isInvalid={errors.license_no}
                        />
                        {errors.license_no && (
                          <Form.Control.Feedback type="invalid">
                            {errors.license_no.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col> */}
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Designation</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("designation", {
                            required: false,
                          })}
                          disabled={!isEditing}
                          isInvalid={errors.designation}
                        />
                        {errors.designation && (
                          <Form.Control.Feedback type="invalid">
                            {errors.designation.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    {/* <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Pay Rate</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("payrate", {
                            required: false,
                          })}
                          disabled={!isEditing}
                          isInvalid={errors.payrate}
                        />
                        {errors.payrate && (
                          <Form.Control.Feedback type="invalid">
                            {errors.payrate.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col> */}
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...register("address", {
                        required: false,
                      })}
                      disabled={!isEditing}
                      isInvalid={errors.address}
                    />
                    {errors.address && (
                      <Form.Control.Feedback type="invalid">
                        {errors.address.message}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="member-since">
                      <strong>Member Since:</strong>{" "}
                      {new Date(employee?.createdAt).toLocaleDateString()}
                    </div>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => setIsChangingPassword(true)}
                      className="change-password-btn"
                    >
                      Change Password
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Change Password Modal */}
        <Modal show={isChangingPassword} onHide={handleCancelPassword} centered>
          <Modal.Header closeButton>
            <Modal.Title>Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={isPasswordVisible.current ? "text" : "password"}
                    {...registerPassword("currentPassword", {
                      required: "Current password is required",
                    })}
                    isInvalid={passwordErrors.currentPassword}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="position-absolute top-50 end-0 translate-middle-y password-toggle-btn"
                    onClick={() => togglePasswordVisibility("current")}
                  >
                    {isPasswordVisible.current ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                {passwordErrors.currentPassword && (
                  <Form.Control.Feedback type="invalid">
                    {passwordErrors.currentPassword.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={isPasswordVisible.new ? "text" : "password"}
                    {...registerPassword("newPassword", {
                      required: "New password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    isInvalid={passwordErrors.newPassword}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="position-absolute top-50 end-0 translate-middle-y password-toggle-btn"
                    onClick={() => togglePasswordVisibility("new")}
                  >
                    {isPasswordVisible.new ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                {passwordErrors.newPassword && (
                  <Form.Control.Feedback type="invalid">
                    {passwordErrors.newPassword.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={isPasswordVisible.confirm ? "text" : "password"}
                    {...registerPassword("confirmPassword", {
                      required: "Please confirm your password",
                      validate: (val) => {
                        if (watchPassword("newPassword") != val) {
                          return "Passwords do not match";
                        }
                      },
                    })}
                    isInvalid={passwordErrors.confirmPassword}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="position-absolute top-50 end-0 translate-middle-y password-toggle-btn"
                    onClick={() => togglePasswordVisibility("confirm")}
                  >
                    {isPasswordVisible.confirm ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                {passwordErrors.confirmPassword && (
                  <Form.Control.Feedback type="invalid">
                    {passwordErrors.confirmPassword.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleCancelPassword}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Change Password
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </Layout>
  );
};

export default EmployeeProfile;
